import React from 'react';

function PromotionTemplate({ title, message, styling, content }) {
  // ✅ STAGE 1 FIX: Use VITE_API_URL from env (works in dev + production)
  const API_URL = import.meta.env.VITE_API_URL || window.location.origin;
  const fontFamily = styling?.fontFamily || 'Poppins';
  const bgColor = styling?.backgroundColor || '#1a1a1a';
  const accentColor = styling?.accentColor || '#FDB44B';
  const textColor = styling?.textColor || '#FFFFFF';
  const mainImage = styling?.mainImage || styling?.announcementImage;
  
  // Editable content with defaults
  const promotionBadge = content?.promotionBadge || 'Special Offer';
  const promotionTagline = content?.promotionTagline || 'Salon • Spa • Ladies Gym';
  
  // Extract discount percentage if in message
  const discountMatch = message?.match(/(\d+)%/);
  const discountPercent = discountMatch ? discountMatch[1] : null;
  
  // ✅ STAGE 4: QR Code settings
  const showQR = content?.ctaEnabled === true;
  const whatsappNumber = '2349055444495';
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=https://wa.me/${whatsappNumber}`;

  // ✅ Check if media is video
  const isVideo = mainImage && (
    mainImage.endsWith('.mp4') ||
    mainImage.endsWith('.webm') ||
    mainImage.endsWith('.ogg')
  );

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        position: 'relative',
        background: bgColor,
        fontFamily: `${fontFamily}, sans-serif`,
        overflow: 'hidden',
      }}
    >
      {/* Diagonal Accent Shape - Right Side */}
      <div
        style={{
          position: 'absolute',
          top: '-10%',
          right: '-5%',
          width: '50%',
          height: '120%',
          background: accentColor,
          transform: 'skewX(-15deg)',
          zIndex: 0,
        }}
      />

      {/* Content Container */}
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Left Section - Text */}
        <div
          style={{
            flex: '0 0 50%',
            padding: '3.5rem 4.5rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          {/* Top Branding */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              marginBottom: '2rem',
            }}
          >
            <div
              style={{
                fontSize: '2.5rem',
              }}
            >
              🎉
            </div>
            <div
              style={{
                fontSize: '1rem',
                fontWeight: '700',
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: accentColor,
              }}
            >
              Special Offer
            </div>
          </div>

          {/* Main Title */}
          <h1
            style={{
              fontSize: styling?.fontSize === 'small' ? '3.2rem' : styling?.fontSize === 'large' ? '5rem' : '4.2rem',
              fontWeight: '900',
              lineHeight: '1',
              color: textColor,
              marginBottom: '2rem',
              letterSpacing: '-0.02em',
              textTransform: 'uppercase',
            }}
          >
            {title || 'SPECIAL OFFER'}
          </h1>

          {/* Discount Badge */}
          {discountPercent && (
            <div
              style={{
                display: 'inline-block',
                background: accentColor,
                color: '#1a1a1a',
                padding: '1.2rem 2.5rem',
                borderRadius: '15px',
                fontSize: '3rem',
                fontWeight: '900',
                marginBottom: '2rem',
                boxShadow: '0 10px 40px rgba(253, 180, 75, 0.4)',
                alignSelf: 'flex-start',
                border: `4px solid ${bgColor}`,
              }}
            >
              {discountPercent}% OFF
            </div>
          )}

          {/* Message */}
          <p
            style={{
              fontSize: '1.5rem',
              lineHeight: '1.5',
              color: textColor,
              opacity: 0.95,
              maxWidth: '500px',
              fontWeight: '500',
              marginBottom: '2rem',
            }}
          >
            {message || 'Limited time offer - Don\'t miss out on this amazing deal!'}
          </p>

          {/* ✅ STAGE 4: QR Code - Conditional */}
          {showQR && (
            <div
              style={{
                marginBottom: '2rem',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '0.8rem',
              }}
            >
              <div
                style={{
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  color: accentColor,
                  opacity: 0.9,
                }}
              >
                Scan to Chat
              </div>
              <img
                src={qrCodeUrl}
                alt="WhatsApp QR Code"
                style={{
                  width: '120px',
                  height: '120px',
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  padding: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                }}
              />
            </div>
          )}

          {/* Chevron Decorations - Left */}
          <div
            style={{
              position: 'absolute',
              bottom: '8%',
              left: '8%',
              display: 'flex',
              gap: '10px',
            }}
          >
            {[...Array(3)].map((_, i) => (
              <svg
                key={i}
                width="45"
                height="65"
                viewBox="0 0 45 65"
                style={{ opacity: 0.3 - i * 0.08 }}
              >
                <path
                  d="M 5 5 L 35 32.5 L 5 60"
                  fill="none"
                  stroke={accentColor}
                  strokeWidth="5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ))}
          </div>
        </div>

        {/* Right Section - Image/Video */}
        <div
          style={{
            flex: '0 0 50%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            padding: '4rem',
            overflow: 'hidden',
          }}
        >
          {mainImage ? (
            <div
              style={{
                position: 'relative',
                width: '100%',
                height: '80%',
                maxWidth: '550px',
              }}
            >
              {/* Media Frame */}
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '25px',
                  overflow: 'hidden',
                  boxShadow: '0 30px 80px rgba(0, 0, 0, 0.6)',
                  border: `6px solid ${accentColor}`,
                }}
              >
                {isVideo ? (
                  <video
                    src={`${API_URL}${mainImage}`}
                    autoPlay
                    loop
                    muted
                    playsInline
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <img
                    src={`${API_URL}${mainImage}`}
                    alt="Promotion"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                )}
              </div>

              {/* Corner Accent - MOVED INSIDE */}
              <div
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  width: '60px',
                  height: '60px',
                  background: accentColor,
                  borderRadius: '50%',
                  opacity: 0.5,
                }}
              />
            </div>
          ) : (
            // ✅ Subtle gradient instead of text placeholder
            <div
              style={{
                width: '100%',
                height: '80%',
                maxWidth: '550px',
                borderRadius: '25px',
                background: `linear-gradient(135deg, ${bgColor}00 0%, ${accentColor}15 100%)`,
                border: `3px solid ${accentColor}30`,
              }}
            />
          )}

          {/* Chevrons - Top Right */}
          <div
            style={{
              position: 'absolute',
              top: '10%',
              right: '10%',
              display: 'flex',
              gap: '10px',
            }}
          >
            {[...Array(3)].map((_, i) => (
              <svg
                key={i}
                width="50"
                height="70"
                viewBox="0 0 50 70"
                style={{ opacity: 0.6 - i * 0.15 }}
              >
                <path
                  d="M 5 5 L 40 35 L 5 65"
                  fill="none"
                  stroke={accentColor}
                  strokeWidth="6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ))}
          </div>

          {/* Chevrons - Bottom Right */}
          <div
            style={{
              position: 'absolute',
              bottom: '10%',
              right: '10%',
              display: 'flex',
              gap: '10px',
              opacity: 0.4,
            }}
          >
            {[...Array(4)].map((_, i) => (
              <svg
                key={i}
                width="40"
                height="60"
                viewBox="0 0 40 60"
                style={{ 
                  opacity: 0.8 - i * 0.15,
                  transform: `scale(${1 - i * 0.1})`
                }}
              >
                <path
                  d="M 5 5 L 30 30 L 5 55"
                  fill="none"
                  stroke={accentColor}
                  strokeWidth="5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ))}
          </div>
        </div>
      </div>

      {/* Decorative Stars/Sparkles */}
      <div
        style={{
          position: 'absolute',
          top: '15%',
          left: '45%',
          fontSize: '2.5rem',
          animation: 'twinkle 2s ease-in-out infinite',
          zIndex: 2,
        }}
      >
        ✨
      </div>
      <div
        style={{
          position: 'absolute',
          bottom: '25%',
          left: '48%',
          fontSize: '2rem',
          animation: 'twinkle 2s ease-in-out infinite 0.5s',
          zIndex: 2,
        }}
      >
        ⭐
      </div>

      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800;900&family=Montserrat:wght@400;500;600;700;800;900&display=swap');
          
          @keyframes twinkle {
            0%, 100% { opacity: 0.3; transform: scale(1) rotate(0deg); }
            50% { opacity: 1; transform: scale(1.2) rotate(180deg); }
          }
        `}
      </style>
    </div>
  );
}

export default PromotionTemplate;